import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import cx from 'classnames';
import { filter, sortBy } from 'lodash';

import styles from './CycleTime.module.scss';
import { sortedInsert, sortedSwap, sortedRemove } from 'js/utils/array';
import { percentile } from 'js/utils/stats';
import { scaleColorTraffic } from 'js/utils/color';
import { useStandardCollator } from 'js/utils/string';
import { onePointPerDay } from './utils';
import { Icon } from 'js/components';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import CycleTimeChart from './CycleTimeChart';
import Metrics from './Metrics';
import DropdownFilter from 'js/components/Filter/DropdownFilter';

// import mockTasks from './mockData';
import { DropdownToggle } from 'reactstrap';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';

const FilterToggle = () => {
  return (
    <DropdownToggle
      className="dropdown-toggle btn-outline-info btn-inline"
      color="null"
      caret={false}
    >
      <Icon icon={faFilter} />
    </DropdownToggle>
  );
};

const CycleTime = ({
  tasks = [],
  movingAverageWindow = 19,
  margins = { top: 20, right: 20, bottom: 20, left: 20 },
  percentiles = [0.5, 0.85, 0.95],
}) => {
  // tasks = mockTasks;
  const collator = useStandardCollator();

  const scaleColorPercentile = useMemo(() => {
    const max = Math.max(...percentiles);
    const min = Math.min(...percentiles);
    return scaleColorTraffic.copy().domain([max, (min + max) / 2, min]);
  }, [percentiles]);

  // Sub-tasks (level -1) and epics and above (level 1+) aren't standard work items, so they
  // start unchecked; types without a level are treated as standard
  const taskTypeLevels = useMemo(
    () => new Map(tasks.map((task) => [task.task_type, task.task_type_level])),
    [tasks]
  );
  const isDefaultTaskType = useCallback(
    (type) => (taskTypeLevels.get(type) ?? 0) === 0,
    [taskTypeLevels]
  );

  // Get all unique task types from the data
  const allTaskTypes = useMemo(() => {
    const taskTypes = new Set();
    tasks.forEach((task) => {
      taskTypes.add(task.task_type);
    });
    return Array.from(taskTypes).sort((a, b) => collator.compare(a, b));
  }, [tasks, collator]);

  const taskTypesPaletteLookup = useMemo(() => {
    return new Map(
      allTaskTypes.map((taskType, index) => [
        taskType,
        `palette-chart-light-${index + 1}`,
      ])
    );
  }, [allTaskTypes]);

  // Convert task types to DropdownFilter format
  const filterOptions = useMemo(() => {
    return allTaskTypes.map((taskType) => ({
      value: taskType,
      text: taskType,
      defaultSelected: isDefaultTaskType(taskType),
    }));
  }, [allTaskTypes, isDefaultTaskType]);

  // Track previous allTaskTypes to detect new types
  const prevAllTaskTypesRef = useRef([]);

  // State for selected task types (initially the standard ones)
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([]);

  // Update selected task types when allTaskTypes changes
  // Auto-select new types based on current selection state
  useEffect(() => {
    const prevAllTaskTypes = prevAllTaskTypesRef.current;

    // If this is the first time we have task types, select the standard ones
    if (prevAllTaskTypes.length === 0 && allTaskTypes.length > 0) {
      setSelectedTaskTypes(allTaskTypes.filter(isDefaultTaskType));
      prevAllTaskTypesRef.current = allTaskTypes;
      return;
    }

    const newTypes = allTaskTypes.filter(
      (type) => !prevAllTaskTypes.includes(type)
    );

    if (newTypes.length > 0) {
      // New types were added
      setSelectedTaskTypes((prevSelected) => {
        // Check if all previous standard types were selected
        const allPreviousTypesSelected = prevAllTaskTypes
          .filter(isDefaultTaskType)
          .every((type) => prevSelected.includes(type));

        if (allPreviousTypesSelected) {
          // The selection wasn't narrowed, so select the new standard types too
          return [...prevSelected, ...newTypes.filter(isDefaultTaskType)];
        } else {
          // Some previous types were not selected, so don't select new types
          // Just keep the currently selected ones that are still valid
          return prevSelected.filter((type) => allTaskTypes.includes(type));
        }
      });
    } else {
      // No new types, just filter out any selected types that no longer exist
      setSelectedTaskTypes((prevSelected) =>
        prevSelected.filter((type) => allTaskTypes.includes(type))
      );
    }

    // Update the ref for next time
    prevAllTaskTypesRef.current = allTaskTypes;
  }, [allTaskTypes, isDefaultTaskType]);

  // Handle filter change from DropdownFilter (receives object with boolean values)
  const handleFilterChange = useCallback((selectedOptions) => {
    const newSelectedTypes = Object.keys(selectedOptions).filter(
      (key) => selectedOptions[key]
    );
    setSelectedTaskTypes(newSelectedTypes);
  }, []);

  // Process tasks data
  const processedData = useMemo(() => {
    if (!tasks || tasks.length === 0)
      return {
        taskTypePaletteLookup: new Map(),
        tasks: [],
        movingAverage: [],
        hasValidTasks: false,
        hasFilteredTasks: false,
      };

    // First filter for valid tasks (with cycle time and data)
    const validTasks = filter(
      tasks,
      (task) =>
        task.cycle_time_seconds &&
        task.created_at &&
        task.cycle_time_seconds > 0
    );

    // Then filter by selected task types
    const filteredTasks = filter(validTasks, (task) =>
      selectedTaskTypes.includes(task.task_type)
    );

    const sortedTasks = sortBy(
      filteredTasks,
      (task) => new Date(task.resolved_at)
    );

    // Calculate moving average
    const movingAverage = [];
    const effectiveMovingAverageWindow = Math.min(
      movingAverageWindow,
      sortedTasks.length
    );
    const halfWindow = Math.floor(effectiveMovingAverageWindow / 2);
    const valueWindow = [];

    for (let i = 0; i < sortedTasks.length + halfWindow; i++) {
      const task = sortedTasks[i];

      if (i < effectiveMovingAverageWindow) {
        sortedInsert(valueWindow, task.cycle_time_seconds);
      } else if (i > sortedTasks.length - 1) {
        sortedRemove(
          valueWindow,
          sortedTasks[i - effectiveMovingAverageWindow].cycle_time_seconds
        );
      } else {
        sortedSwap(
          valueWindow,
          sortedTasks[i - effectiveMovingAverageWindow].cycle_time_seconds,
          task.cycle_time_seconds
        );
      }

      const windowTasks = valueWindow;
      const meanCycleTime = windowTasks.reduce(
        (sum, task) => sum + task.cycle_time_seconds / windowTasks.length,
        0
      );

      const val = {
        date: new Date(
          sortedTasks[i - halfWindow]?.resolved_at ?? new Date('1970-01-01')
        ),
        meanCycleTime: meanCycleTime,
        index: i - halfWindow,
      };
      percentiles.forEach((x) => {
        val[x] = percentile(valueWindow, x);
      });

      movingAverage.push(val);
    }

    const taskTypePaletteLookup = new Map(
      selectedTaskTypes
        .sort((a, b) => collator.compare(a, b))
        .map((t, i) => [
          t,
          taskTypesPaletteLookup.get(t) || `palette-chart-light-${i + 1}`,
        ])
    );

    return {
      taskTypePaletteLookup,
      tasks: sortedTasks.map((task, index) => ({
        ...task,
        date: new Date(task.resolved_at),
        index,
      })),
      // Several tasks resolved on one day would otherwise make the lines jump within the day
      movingAverage: onePointPerDay(movingAverage.slice(-sortedTasks.length)),
      hasValidTasks: validTasks.length > 0,
      hasFilteredTasks: filteredTasks.length > 0,
    };
  }, [
    tasks,
    movingAverageWindow,
    selectedTaskTypes,
    collator,
    percentiles,
    taskTypesPaletteLookup,
  ]);

  if (!processedData.hasValidTasks) {
    return (
      <div className={styles['cycle-time-chart-container']}>
        <div className={styles['no-data']}>
          <div className={styles['no-data-title']}>
            No cycle time data available
          </div>
          <div className={styles['no-data-subtitle']}>
            Tasks need cycle time data to appear in this chart
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['cycle-time-chart-container']}>
      <div className={styles['task-types']}>
        <DropdownFilter
          name="Task Types"
          filterOptions={filterOptions}
          onChange={handleFilterChange}
          className="compact"
          renderToggle={FilterToggle}
        />
        {[...processedData.taskTypePaletteLookup].map(([taskType, palette]) => (
          <div key={taskType} className={styles['task-type']}>
            <span className={palette}>
              <Icon
                icon={faCircleSmall}
                className="palette-text mr-1"
                transform="grow-1"
              />
            </span>
            {taskType}
          </div>
        ))}
      </div>
      <div className={cx('chart-bounding-box', styles['cycle-time-chart'])}>
        <div className={styles['chart-wrapper']}>
          <CycleTimeChart
            margins={margins}
            processedData={processedData}
            percentiles={percentiles}
            scaleColorPercentile={scaleColorPercentile}
          />
          {!processedData.hasFilteredTasks && (
            <div className="no-response-overlay">
              <div>No results</div>
            </div>
          )}
        </div>
      </div>
      <div className={styles['metrics']}>
        {processedData.hasFilteredTasks && (
          <Metrics percentiles={percentiles} processedData={processedData} />
        )}
      </div>
      <div className={styles['percentiles']}>
        {percentiles.map((x) => (
          <div key={x} className={styles['percentile']}>
            <div
              className={styles['percentile-line']}
              style={{ backgroundColor: scaleColorPercentile(x) }}
            ></div>
            {/* Percentiles over a window of tasks around each point, not averages */}
            <div>Rolling {Math.round(x * 100)}th percentile</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CycleTime;
